import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/dashboard.css";
import logo from "../assets/primerise-logo.png";
import poster1 from "../assets/posters/poster1.jpg";
import MarketSection from "../components/MarketSection";

const API_BASE_URL = import.meta.env.VITE_API_BASE || "https://primerise.space";
const WHATSAPP_URL = "https://wa.me/18304488973";

const WhatsAppIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 32 32"
    fill="currentColor"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M16.04 2.67C8.7 2.67 2.73 8.63 2.73 15.98c0 2.35.62 4.64 1.79 6.66L2.67 29.33l6.85-1.8a13.22 13.22 0 0 0 6.52 1.66h.01c7.34 0 13.31-5.97 13.31-13.31 0-3.56-1.39-6.9-3.9-9.42a13.23 13.23 0 0 0-9.42-3.79Zm.01 24.27h-.01c-2.06 0-4.08-.55-5.84-1.6l-.42-.25-4.06 1.07 1.08-3.96-.27-.43a10.96 10.96 0 0 1-1.67-5.79C4.86 9.82 9.87 4.81 16.04 4.81c2.98 0 5.79 1.16 7.9 3.27a11.1 11.1 0 0 1 3.28 7.9c0 6.16-5.01 10.96-11.17 10.96Zm6.13-8.21c-.34-.17-1.98-.98-2.29-1.09-.31-.11-.53-.17-.75.17-.22.34-.86 1.09-1.05 1.31-.19.22-.39.25-.72.08-.34-.17-1.42-.52-2.7-1.67-1-.89-1.67-1.99-1.87-2.33-.19-.34-.02-.52.15-.69.15-.15.34-.39.5-.58.17-.19.22-.34.34-.56.11-.22.06-.42-.03-.58-.08-.17-.75-1.81-1.03-2.48-.27-.65-.55-.56-.75-.57h-.64c-.22 0-.58.08-.89.42-.31.34-1.17 1.14-1.17 2.78s1.2 3.23 1.36 3.45c.17.22 2.36 3.6 5.72 5.05.8.34 1.42.55 1.91.7.8.25 1.53.22 2.11.13.64-.1 1.98-.81 2.26-1.59.28-.78.28-1.45.2-1.59-.08-.14-.31-.22-.65-.39Z" />
  </svg>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const getToken = () => {
    return (
      localStorage.getItem("token") ||
      localStorage.getItem("primeriseToken") ||
      localStorage.getItem("naxoraToken") ||
      ""
    );
  };

  useEffect(() => {
    const viewport = document.querySelector('meta[name="viewport"]');

    if (viewport) {
      viewport.setAttribute(
        "content",
        "width=device-width, initial-scale=1, viewport-fit=cover"
      );
    }
  }, []);

  const handleLogout = () => {
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("primeriseToken");
      localStorage.removeItem("primeriseAuth");
      localStorage.removeItem("primeriseCurrentUser");

      localStorage.removeItem("naxoraToken");
      localStorage.removeItem("naxoraAuth");
      localStorage.removeItem("naxoraCurrentUser");

      localStorage.removeItem("isAuthenticated");
      localStorage.removeItem("currentUser");
      localStorage.removeItem("user");

      sessionStorage.clear();
      setUser(null);
      window.location.replace("/login");
    } catch (error) {
      console.error("Logout error:", error);
      window.location.replace("/login");
    }
  };

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const loadProfileOnce = async () => {
      try {
        const token = getToken();

        if (!token) {
          navigate("/login", { replace: true });
          return;
        }

        const response = await fetch(`${API_BASE_URL}/api/auth/profile`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
        });

        let data = {};
        try {
          data = await response.json();
        } catch {
          data = {};
        }

        if (!response.ok || !data.user) {
          handleLogout();
          return;
        }

        if (!isMounted) return;

        setUser(data.user);

        localStorage.setItem("primeriseAuth", "true");
        localStorage.setItem("isAuthenticated", "true");
        localStorage.setItem("primeriseCurrentUser", JSON.stringify(data.user));
        localStorage.setItem("currentUser", JSON.stringify(data.user));
        localStorage.setItem("user", JSON.stringify(data.user));

        // Old keys kept for copied Naxora auth guards
        localStorage.setItem("naxoraAuth", "true");
        localStorage.setItem("naxoraCurrentUser", JSON.stringify(data.user));
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Auth/profile error:", error);
          handleLogout();
        }
      } finally {
        if (isMounted) {
          setLoadingUser(false);
        }
      }
    };

    loadProfileOnce();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [navigate]);

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const handleDeposit = () => {
    closeSidebar();
    navigate("/deposit");
  };

  const handleWithdraw = () => {
    closeSidebar();
    navigate("/withdraw");
  };

  const handleTeam = () => {
    closeSidebar();
    navigate("/team");
  };

  const handleHistory = () => {
    closeSidebar();
    navigate("/history");
  };

  const handleProfile = () => {
    closeSidebar();
    navigate("/profile");
  };

  const handleTrade = () => {
    closeSidebar();
    navigate("/copytrade");
  };

  const handleWhatsApp = () => {
    closeSidebar();
    window.open(WHATSAPP_URL, "_blank", "noopener,noreferrer");
  };

  const handleHome = () => {
    closeSidebar();
    navigate("/dashboard");
  };

  const formatPrice = (price) => {
    const numericPrice = Number(price || 0);

    if (numericPrice >= 1000) {
      return `$${numericPrice.toLocaleString(undefined, {
        maximumFractionDigits: 2,
      })}`;
    }

    if (numericPrice >= 1) {
      return `$${numericPrice.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }

    if (numericPrice >= 0.01) {
      return `$${numericPrice.toFixed(4)}`;
    }

    return `$${numericPrice.toFixed(6)}`;
  };

  if (loadingUser || !user) {
    return (
      <div className="dashboard-page">
        <div className="dashboard-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <button
        type="button"
        className="mobile-menu-toggle"
        onClick={() => setSidebarOpen(true)}
        aria-label="Open menu"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      <div
        className={`sidebar-overlay ${sidebarOpen ? "show" : ""}`}
        onClick={closeSidebar}
      ></div>

      <aside className={`dashboard-sidebar ${sidebarOpen ? "open" : ""}`}>
        <div className="sidebar-top">
          <img src={logo} alt="Primerise" className="sidebar-logo" />

          <button
            type="button"
            className="sidebar-close-btn"
            onClick={closeSidebar}
            aria-label="Close menu"
          >
            ×
          </button>
        </div>

        <div className="sidebar-menu">
          <button
            className="sidebar-item active"
            type="button"
            onClick={handleHome}
            title="Home"
            aria-label="Home"
          >
            <i className="fa-solid fa-house"></i>
            <span>Home</span>
          </button>

          <button
            className="sidebar-item"
            type="button"
            onClick={handleDeposit}
            title="Deposit"
            aria-label="Deposit"
          >
            <i className="fa-solid fa-money-bill-transfer"></i>
            <span>Deposit</span>
          </button>

          <button
            className="sidebar-item"
            type="button"
            onClick={handleWithdraw}
            title="Withdraw"
            aria-label="Withdraw"
          >
            <i className="fa-solid fa-money-bill-wave"></i>
            <span>Withdraw</span>
          </button>

          <button
            className="sidebar-item"
            type="button"
            onClick={handleTeam}
            title="Team"
            aria-label="Team"
          >
            <i className="fa-solid fa-users"></i>
            <span>Team</span>
          </button>

          <button
            className="sidebar-item"
            type="button"
            onClick={handleHistory}
            title="History"
            aria-label="History"
          >
            <i className="fa-solid fa-clock-rotate-left"></i>
            <span>History</span>
          </button>

          <button
            className="sidebar-item"
            type="button"
            onClick={handleProfile}
            title="Profile"
            aria-label="Profile"
          >
            <i className="fa-solid fa-user"></i>
            <span>Profile</span>
          </button>

          <button
            className="sidebar-item"
            type="button"
            onClick={handleTrade}
            title="Trade"
            aria-label="Trade"
          >
            <i className="fa-solid fa-chart-line"></i>
            <span>Trade</span>
          </button>

          <button
            className="sidebar-item"
            type="button"
            onClick={handleWhatsApp}
            title="WhatsApp Support"
            aria-label="WhatsApp Support"
          >
            <WhatsAppIcon />
            <span>WhatsApp</span>
          </button>

          <button
            className="sidebar-logout sidebar-logout-golden"
            onClick={handleLogout}
            type="button"
            title="Logout"
            aria-label="Logout"
          >
            <i className="fa-solid fa-right-from-bracket"></i>
            <span>Logout</span>
          </button>
        </div>
      </aside>

      <main className="dashboard-main">
        <section className="dashboard-topbar">
          <div className="user-balance-card">
            <div className="user-balance-left">
              <span className="balance-label">Total Balance</span>
              <h2>{formatPrice(user.balance)}</h2>
              <p>{user.name || user.username || "User"}</p>
            </div>

            <div className="user-balance-right">
              <div className="user-avatar">
                {(user.name || user.username || "U").charAt(0).toUpperCase()}
              </div>
            </div>
          </div>
        </section>

        <section className="dashboard-action-row">
          <button
            className="action-btn deposit-btn"
            type="button"
            onClick={handleDeposit}
          >
            <i className="fa-solid fa-arrow-down"></i>
            <span>Deposit</span>
          </button>

          <button
            className="action-btn withdraw-btn"
            type="button"
            onClick={handleWithdraw}
          >
            <i className="fa-solid fa-arrow-up"></i>
            <span>Withdraw</span>
          </button>
        </section>

        <section className="dashboard-poster-single">
          <div className="poster-card poster-card-small">
            <img src={poster1} alt="Primerise Poster" />
          </div>
        </section>

        <MarketSection />

        <nav className="dashboard-bottom-nav">
          <button
            className="bottom-nav-btn"
            type="button"
            onClick={handleTeam}
            aria-label="Team"
          >
            <i className="fa-solid fa-users"></i>
            <span>Team</span>
          </button>

          <button
            className="bottom-nav-btn"
            type="button"
            onClick={handleHistory}
            aria-label="History"
          >
            <i className="fa-solid fa-clock-rotate-left"></i>
            <span>History</span>
          </button>

          <button
            className="bottom-nav-btn bottom-nav-main"
            type="button"
            onClick={handleTrade}
            aria-label="Trade"
          >
            <i className="fa-solid fa-chart-simple"></i>
            <span>Trade</span>
          </button>

          <button
            className="bottom-nav-btn"
            type="button"
            onClick={handleProfile}
            aria-label="Profile"
          >
            <i className="fa-solid fa-user"></i>
            <span>Profile</span>
          </button>
        </nav>
      </main>
    </div>
  );
};

export default Dashboard;